import { isEmpty, partition } from 'lodash';
import { getElementId, getTitle } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { Tag } from '../../../../../../common/tags/tagTypes';
import {
    BoardVirtualPathMap,
    TaggedElementsData,
} from '../../../../../../common/api/tags/tagElementsRetrievalRouteTypes';

export const isTagMultiContext = (tag: Tag): boolean => tag.rootBoardIds?.length > 1;

// Find the id of the board in the current share context that the tag is associated with
// It will return the lowest level board id in the shareRootPath that matches one of the tag's rootBoardIds
export const getCurrentContextRootId = (tag: Tag, shareRootPath: ImMNElement[]): string | undefined => {
    // Search from the bottom of the share path to find the closest matching rootBoardId
    const boardIds = shareRootPath.map((element) => getElementId(element)).reverse();
    return tag.rootBoardIds.find((id) => boardIds.includes(id));
};

export const getHighestTagBoardId = (tag: Tag, userRootPath: ImMNElement[]): string => {
    if (!isTagMultiContext(tag)) {
        return tag.rootBoardIds[0];
    }

    for (const board of userRootPath) {
        const id = getElementId(board);
        if (tag.rootBoardIds.includes(id)) {
            return id;
        }
    }

    return tag.rootBoardIds[0];
};

export const createIsTagInPath = (path: ImMNElement[]) => {
    const pathIds = new Set(path.map(getElementId));

    return (tag: Tag) => tag.rootBoardIds.some((id) => pathIds.has(id));
};

type TagsByBoards = {
    title: string;
    tags: Tag[];
}[];

export const generateTagsByBoardsArray = (
    allTags: Tag[],
    userRootPath: ImMNElement[],
    searchString: string,
): TagsByBoards => {
    const searchValue = searchString.toLowerCase();
    const isIncludedInSearch = (tag: Tag) => isEmpty(searchValue) || tag.name.toLowerCase().includes(searchValue);
    const isTagInPath = createIsTagInPath(userRootPath);

    const filteredTags = allTags.filter((tag) => isIncludedInSearch(tag) && isTagInPath(tag));

    const tagsByBoards: TagsByBoards = userRootPath.reduce((acc: TagsByBoards, board: ImMNElement) => {
        const boardId = getElementId(board);
        const tags: Tag[] = [];

        for (let i = filteredTags.length - 1; i >= 0; i--) {
            const tag = filteredTags[i];

            // Add the tag to the current board, and remove it from the list so that it can't be added to any other boards
            if (tag.rootBoardIds.includes(boardId)) {
                tags.push(tag);
                filteredTags.splice(i, 1);
            }
        }

        if (!tags.length) return acc;

        const title = getTitle(board);
        acc.push({ title, tags });

        return acc;
    }, []);

    return tagsByBoards;
};

// Check if the current tag root is used for applying the tag on any element within the current context
export const isTagRootUsed = (
    taggedElements: TaggedElementsData[],
    contextRootsInCurrentPath: string[],
    tagRootId: string,
    boardVirtualPathToRootMap: BoardVirtualPathMap,
): boolean => {
    return taggedElements.some((el) => {
        if (!el.elementParentId) return false;

        const elementPath = boardVirtualPathToRootMap[el.elementParentId];
        const contextRoot = elementPath.find((boardId) => contextRootsInCurrentPath.includes(boardId));

        for (let i = 0; i < elementPath.length; i++) {
            const boardId = elementPath[i];
            if (boardId === tagRootId) return true;
            if (boardId === contextRoot) return false;
        }
    });
};

// Returns 2 lists of tag roots split by whether they sit under the current editing tag or not, which determines whether they can be updated
export const partitionTagRootsByEditability = (
    currentlyEditingTag: Tag,
    userRootPath: ImMNElement[],
    boardVirtualPathToRootMap: BoardVirtualPathMap,
) => {
    const highestRootId = getHighestTagBoardId(currentlyEditingTag, userRootPath);
    const [tagRootsToUpdate, tagRootsNotToUpdate] = partition(currentlyEditingTag.rootBoardIds, (rootBoardId) => {
        const pathToRoot = boardVirtualPathToRootMap?.[rootBoardId];
        return pathToRoot && pathToRoot.includes(highestRootId);
    });

    return { tagRootsToUpdate, tagRootsNotToUpdate };
};

export const getElementIdsForUpdate = (
    taggedElements: TaggedElementsData[],
    editingRootId: string,
    boardVirtualPathToRootMap: BoardVirtualPathMap | null,
) => {
    return taggedElements
        .filter((el) => {
            if (!el.elementParentId) return false;

            const pathToRoot = boardVirtualPathToRootMap?.[el.elementParentId];
            return pathToRoot && pathToRoot.includes(editingRootId);
        })
        .map((el) => el.elementId);
};
